import { useMemo, useCallback } from "react";
import { useSchedule } from "@/contexts/ScheduleContext";
import { useCategories } from "@/hooks/useCategories";
import { TEAM } from "@/data/team";
import { EVENT_CATEGORIES } from "@/data/categories";
import { findSlots, formatTime } from "@/utils/scheduling";
import { apiPost } from "@/utils/api";
import type { TimeSlot, TeamMember } from "@/types";

// ── MOAT #3: Silent learning signal helper ──
const recordSignal = (signalType: string, data: Record<string, unknown>) => {
  apiPost("/api/ai/record-signal", { signalType, data }).catch(() => {});
};

/**
 * Custom hook that extracts computed state and handler callbacks
 * from the main App component, keeping App.tsx thin.
 */
export function useScheduleHandlers() {
  const { state, dispatch, isInterview } = useSchedule();
  const categories = useCategories();

  // ─── COMPUTED STATE ──────────────────────────────────────────
  const selectedTeam = useMemo(() => state.sel.map((id) => TEAM.find((m) => m.id === id)!), [state.sel]);

  const slots = useMemo(() => {
    if (!state.sel.length || !state.dur) return [];
    return findSlots(state.sel, state.dur.mins, state.tentFree);
  }, [state.sel, state.dur, state.tentFree]);

  const orgUsersAsTeamMembers = useMemo(() => {
    return state.orgUsers.map((user) => ({
      id: user.id,
      name: user.displayName,
      email: user.mail || "",
      role: user.jobTitle || "Team Member",
      avatar: user.photo || "",
      color: "#7C3AED",
      location: user.officeLocation || "",
      group: "org",
      events: [],
    }));
  }, [state.orgUsers]);

  // ─── HANDLERS ────────────────────────────────────────────────
  const selectCategory = useCallback(
    (catId: string) => dispatch({ type: "SELECT_CATEGORY", category: catId }),
    [dispatch]
  );

  const selectSubcategory = useCallback(
    (subId: string) => {
      const cat = categories.find((c) => c.id === state.category);
      const sub = cat?.subcategories.find((s) => s.id === subId);
      dispatch({
        type: "SELECT_SUBCATEGORY",
        subcategory: subId,
        defaultDur: sub?.defaultDur,
        ids: sub?.ids,
      });
    },
    [dispatch, state.category, categories]
  );

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    dispatch({ type: "SET_FIELD", field: "copied", value: true });
    setTimeout(() => {
      dispatch({ type: "SET_FIELD", field: "copied", value: false });
    }, 2000);
  }, [dispatch]);

  const mkParticipantTpl = useCallback(
    (participant: TeamMember) => {
      const sub = EVENT_CATEGORIES.find((c) => c.id === state.category)?.subcategories.find(
        (s) => s.id === state.subcategory
      );
      const videoLine =
        state.videoPlatform === "none"
          ? ""
          : state.videoPlatform === "custom"
            ? `Video: ${state.customVideoLink}`
            : `Video: ${state.videoPlatform.toUpperCase()} call`;
      return `Dear ${participant.name},

You're invited to our ${sub?.label || "event"}: ${state.title || "TBD"}

Time: ${state.chosen ? `${formatTime(state.chosen.utcStart)} - ${formatTime(state.chosen.utcEnd)}` : "TBD"}
${videoLine}

Other participants: ${selectedTeam.map((m) => m.name).join(", ")}

${state.notes ? `Notes: ${state.notes}` : ""}

Looking forward to speaking with you!`;
    },
    [
      state.category,
      state.subcategory,
      state.videoPlatform,
      state.customVideoLink,
      state.title,
      state.chosen,
      state.notes,
      selectedTeam,
    ]
  );

  const mkCandidateTpl = useCallback(() => {
    const sub = EVENT_CATEGORIES.find((c) => c.id === state.category)?.subcategories.find(
      (s) => s.id === state.subcategory
    );
    const videoLine =
      state.videoPlatform === "none"
        ? ""
        : state.videoPlatform === "custom"
          ? `Video: ${state.customVideoLink}`
          : `Video: ${state.videoPlatform.toUpperCase()} call`;
    return `Dear ${state.candidate || "Candidate"},

We're excited to move forward with your ${state.position || "application"}!

${sub?.label || "Event"}: ${state.title || "TBD"}
Time: ${state.chosen ? `${formatTime(state.chosen.utcStart)} - ${formatTime(state.chosen.utcEnd)}` : "TBD"}
${videoLine}

You'll be meeting with: ${selectedTeam.map((m) => m.name).join(", ")}

${state.notes ? `Notes: ${state.notes}` : ""}

Best regards`;
  }, [
    state.category,
    state.subcategory,
    state.videoPlatform,
    state.customVideoLink,
    state.candidate,
    state.position,
    state.title,
    state.chosen,
    state.notes,
    selectedTeam,
  ]);

  const switchPreview = useCallback(
    (person: TeamMember) => {
      dispatch({ type: "SET_FIELD", field: "previewPerson", value: person });
      dispatch({ type: "SET_FIELD", field: "previewTab", value: "participant" });
    },
    [dispatch]
  );

  const book = useCallback(
    (slot: TimeSlot) => {
      dispatch({ type: "BOOK_SLOT", slot });
      setTimeout(() => {
        if (selectedTeam.length > 0) {
          dispatch({ type: "SET_FIELD", field: "interviewerTpl", value: mkParticipantTpl(selectedTeam[0]) });
        }
        dispatch({ type: "SET_FIELD", field: "candidateTpl", value: mkCandidateTpl() });
      }, 0);

      // ── MOAT #3: Record slot pick — strongest signal (explicit time choice) ──
      recordSignal("slot_pick", {
        slotTime: new Date(slot.utcStart).toISOString(),
        duration: state.dur,
        category: state.category,
      });
    },
    [selectedTeam, mkParticipantTpl, mkCandidateTpl, dispatch, state.dur, state.category]
  );

  const confirm = useCallback(() => {
    const emails = selectedTeam.map((m) => m.email);
    if (state.candidate && state.candidateEmail) emails.push(state.candidateEmail);
    dispatch({ type: "CONFIRM_BOOKING", emails });

    // ── MOAT #3: Record full booking signal ──
    recordSignal("booking", {
      startTime: state.chosen ? new Date(state.chosen.utcStart).toISOString() : undefined,
      duration: state.dur,
      category: state.category,
      subcategory: state.subcategory,
      people: state.sel,
      platform: state.videoPlatform,
    });

    // Record duration choice (did they change from default?)
    recordSignal("duration_choice", {
      duration: state.dur,
      category: state.category,
      wasDefault: false, // they had the chance to change it
    });

    setTimeout(() => {
      dispatch({ type: "FINISH_SYNC" });
    }, 2000);
  }, [
    selectedTeam,
    state.candidate,
    state.candidateEmail,
    state.chosen,
    state.dur,
    state.category,
    state.subcategory,
    state.sel,
    state.videoPlatform,
    dispatch,
  ]);

  const handleNlpAction = useCallback(
    (result: any) => {
      dispatch({ type: "NLP_ACTION", result });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [dispatch]
  );

  const reset = useCallback(() => dispatch({ type: "RESET" }), [dispatch]);

  return {
    state,
    dispatch,
    isInterview,
    categories,
    selectedTeam,
    slots,
    orgUsersAsTeamMembers,
    selectCategory,
    selectSubcategory,
    copyLink,
    switchPreview,
    book,
    confirm,
    handleNlpAction,
    reset,
  };
}
